import React, { useContext, useEffect, useMemo, useState } from "react";

import { Content, SignUpBox } from "./SignUpBox";
import { SimpleFormView } from "../core/views/UForm";
import { Spin } from "../core/views/Spin";
import { Button } from "../core/views/Button";
import { googleAuthenticationPayload, OrSeparator } from "./SocialLogin";
import { GlobalContext } from "../GlobalContext";
import Box from "@mui/material/Box";
import { UFormField } from "../core/views/UFormField";
import usePopulateBICookie from "../core/hooks/usePopulateBICookie";
import { useHistory, useLocation } from "react-router";
import { RequestError } from "../core/api/RequestError";
import { FormFields } from "../core/api/contracts/FormFields";
import { EmptyLoginState, UserInfo } from "../core/api/contracts/User";
import { HubspotSignupForm, useHubSpotInformer } from "./Auth";
import { AzureSso } from "./login/azure";
import { AuthenticationResult } from "@azure/msal-browser";
import { authenticationResultToObject } from "./login/azure/AzureAuthenticationObject";
import { GoogleLegal } from "./GoogleLegal";
import { UpsolverLegal } from "./UpsolverLegal";
import useVerificationErrorHandler from "./useVerificationErrorHandler";
import { useReCaptcha } from "../core/hooks/useReCaptcha";
import { LoginReply } from "../core/api/contracts/login";
import { useLoginStateProvider } from "../LoginStateProvider";
import GoogleRedirectButton from "./GoogleRedirectButton";
import UserTrackingEvent from "../analytics/UserTrackingEvent";
import useUserTrackingErrorEvent from "../analytics/useUserTrackingErrorEvent";
import UserTrackingFormOnBlur from "../analytics/UserTrackingFormOnBlur";
import { useTagManager } from "../core/hooks/useTagManager";
import useUserAbTests from "../useUserAbTests";
import useUserTrackingEvent from "../analytics/useUserTrackingEvent";

type SignupFormValues = {
  name: string;
  email: string;
  phone: string;
  company: string;
  password: string;
  captcha?: unknown;
};

const toHubspotSignUpForm = (values: SignupFormValues): HubspotSignupForm => {
  const names = values.name.split(" ");
  const hasFullName = names.length > 0;
  return {
    firstname: hasFullName ? names[0] : values.name,
    lastname: names.slice(1).join(" "),
    email: values.email,
    company: values.company,
    phone: values.phone,
  };
};

function useSignupForm(loginState?: { hint: { userInfo: UserInfo } }) {
  const userAbTests = useUserAbTests("mandatory phone number in signup");
  const [formConfig, setFormConfig] = useState<FormFields>();
  const trackSso = useUserTrackingEvent("filled-in-signup");

  useEffect(() => {
    if (userAbTests.status === "loading") {
      return null;
    }
    const hasState = loginState?.hint?.userInfo?.name != null;
    const abTestRule =
      userAbTests.status === "found" && userAbTests.tests["mandatory phone number in signup"].variant === "optional"
        ? ""
        : "required";

    const phoneNumberRule = abTestRule ?? "required";
    if (hasState) {
      trackSso({
        boxFilled: "sso",
        value: { name: loginState.hint.userInfo.name, email: loginState.hint.userInfo.email },
      });
    }
    setFormConfig(
      new FormFields()
        .addField(
          "name",
          "required|string",
          "Full Name",
          "text",
          null,
          loginState.hint.userInfo.name,
          null,
          true,
          null,
          null,
          null,
          null,
          null,
          null,
          "First and last name"
        )
        .addField(
          "email",
          "required|email",
          "Email",
          "text",
          null,
          loginState.hint.userInfo.email,
          null,
          true,
          null,
          false,
          null,
          false,
          null,
          hasState
        )
        .addField("company", "required|string", "Company Name", "text", null, null, null, true)
        .addField("phone", phoneNumberRule, "Phone Number", "text", null, null, null, true)
        .addField(
          "password",
          !hasState && "required|min:5",
          "Password",
          "password",
          null,
          hasState ? "" : null,
          null,
          null,
          null,
          hasState
        )
    );
  }, [loginState, userAbTests]);

  return { formConfig };
}

const userSignupSideContent: Content[] = [
  {
    header: "A no code data lake engineering platform",
    specs: [
      "Visual SQL IDE to design, run and monitor analytics pipelines",
      "Ingest and transform batch and streaming data",
      "Prepare data for query engines or external data stores",
      "Performance optimization: Automated Parquet partitioning, compaction and orchestration",
    ],
  },
  {
    header: "Free forever with your Community license",
    specs: [
      "Free 20 GB storage to get you up and running",
      "Integrate with your AWS or Azure account to use your data (unlimited storage)",
      "Premium support for first 14 days",
      "Guided proof-of-concept available on request",
    ],
  },
];

export default function SignUpView() {
  const context = useContext(GlobalContext);
  const location = useLocation();
  const hubSpotInformer = useHubSpotInformer();
  const history = useHistory();
  const tyrHandleVerificationError = useVerificationErrorHandler();
  const loginStateProvider = useLoginStateProvider();

  const { execute } = useReCaptcha("sign_up");
  const { dataLayer } = useTagManager("GTM-N6FS4FB");

  const [loading, setLoading] = useState<boolean>();

  const [loginState, setLoginState] = useState(() => {
    const currLoginState = location.state as { original: never; hint: never };
    if (currLoginState && currLoginState.original && currLoginState.hint) {
      return currLoginState;
    } else {
      return EmptyLoginState;
    }
  });

  const redirectLoginState = useMemo(() => {
    const hintParam = new URLSearchParams(history.location.search).get("hint");
    const parsed = JSON.parse(hintParam);
    return hintParam ? { original: parsed.authenticationMethod, hint: parsed } : null;
  }, [history.location.search]);
  const sendSignupFailed = useUserTrackingErrorEvent("signup-failed");

  const { formConfig } = useSignupForm(redirectLoginState ?? loginState);

  const form = useMemo(() => formConfig?.createForm(), [formConfig]);

  usePopulateBICookie();

  const onUserOrgs = async (values: SignupFormValues, reply: LoginReply) => {
    dataLayer({ event: "ce_form_submit_success" });

    await hubSpotInformer({ ...values, organization: values.company, role: "admin" }, values.company);
    await loginStateProvider.handleLoginReply(reply);
    setLoading(false);

    const { location } = window;
    window.location.href = location.href.replace(location.pathname, "");
  };

  const signUp = async (values: SignupFormValues) => {
    setLoading(true);
    let token: string;

    try {
      token = await execute();
    } catch (e) {
      console.warn(e);
      token = "";
    }
    try {
      const signupRequest = {
        admin: {
          // @ts-ignore
          info: {
            name: values.name,
            email: values.email,
            phone: values.phone,
            company: values.company,
          },
          authenticationMethod: (redirectLoginState ?? loginState).hint.authenticationMethod,
          password: values.password,
        },
        ...(token && { captcha: token }),
      };
      const reply = await context.auth.signUpUserV3(signupRequest);
      await onUserOrgs(values, reply);
    } catch (err) {
      sendSignupFailed(err);
      if (!tyrHandleVerificationError(err as RequestError)) {
        form.onValidateError((err as RequestError)?.response?.body, { override: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const google = (obj: any) => {
    context.auth
      .authMethodLogin(googleAuthenticationPayload(obj), "google", (x: RequestError) => {
        setLoginState({ original: obj, hint: x.response.body });
        location.state = { original: obj, hint: x.response.body };
      })
      .then(loginStateProvider.handleLoginReply);
  };

  const azure = (authenticationResult: AuthenticationResult) => {
    const authPayload = authenticationResultToObject(authenticationResult);
    return context.auth
      .authMethodLogin(authPayload, "azure", (x) => {
        const state = {
          original: authPayload,
          hint: x.response.body,
          ...Object.fromEntries(new URLSearchParams(history.location.search).entries()),
          marketplace: "azure",
        };
        setLoginState(state);
        location.state = state;
      })
      .then(loginStateProvider.handleLoginReply);
  };

  const hasState = (redirectLoginState || loginState)?.hint?.userInfo?.name != null;

  return (
    <UserTrackingEvent event="signup-started">
      <Spin spinning={!form}>
        {form && (
          <SignUpBox contentDetails={userSignupSideContent}>
            <div className="create-feature-form">
              {!hasState && (
                <>
                  <GoogleRedirectButton />
                  <span className="button-azure">
                    <AzureSso onAuthenticated={azure} />
                  </span>
                  <OrSeparator />
                </>
              )}

              <UserTrackingFormOnBlur eventName="filled-in-signup" form={form}>
                <SimpleFormView<SignupFormValues> form={form} onSuccess={signUp} disabled={loading}>
                  <UFormField input={form.$("email")} />
                  <UFormField input={form.$("password")} />
                  <UFormField input={form.$("name")} />
                  <UFormField input={form.$("company")} />
                  <UFormField input={form.$("phone")} />

                  <Box width="100%" textAlign="center" mt={3}>
                    <Button type="submit" loading={loading}>
                      Sign Up
                    </Button>
                    <UpsolverLegal />
                  </Box>
                </SimpleFormView>
              </UserTrackingFormOnBlur>
            </div>

            <div className="link">
              Already have an account? <a href="/login">Log In</a>
            </div>

            <span className="legal">
              <GoogleLegal />
            </span>
          </SignUpBox>
        )}
      </Spin>
    </UserTrackingEvent>
  );
}
